import React, { useEffect, useState } from "react";
import {
    Box,
    Grid,
    Typography,
    Paper,
    IconButton,
    Tooltip,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { useForm } from "react-hook-form";

import WSSelectField from "../../../Components/Select/WSSelect";
import WSTextField, { TextType } from "../../../Components/TextBox/WSTextBox";
import { callAPI, Method, fetchAllPaginated } from "../../../commonFunctions/CommonFunctions";
import { useSnackbar } from "../../../Components/Snackbar/SnackBarContext";
import { useNavigate, useParams } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";


interface Item {
    ItemId: string;
    ItemName: string;
    ItemCode: string;
    IsActive: boolean;
}

interface Warehouse {
    WarehouseId: string;
    WarehouseName: string;
    Location: string;
    IsActive: boolean;
}

interface StockVerificationData {
    ItemId: string;
    WarehouseId: string;
    PhysicalQty: number;
}

const Verification = () => {
    const [loading, setLoading] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedId, setSelectedId] = useState<string | number>(0);
    const [isPopupOpen, setPopupOpen] = useState(false);
    const { showMessage } = useSnackbar();
    const params = useParams();
    const id = params.id;
    const navigate = useNavigate();

    // ✅ Properly typed state
    const [items, setItems] = useState<Item[]>([]);
    const [warehouses, setWarehouses] = useState<Warehouse[]>([]);

    const {
        control,
        handleSubmit,
        reset,
        formState: { isValid },
    } = useForm({
        mode: "onChange",
        defaultValues: {
            ItemId: "",
            WarehouseId: "",
            PhysicalQty: "",

        },
    });


    const fetchAllData = (url: string) => fetchAllPaginated(url);

    const fetchDropdownData = async () => {
    try {
        setLoading(true);

        const [allItems, allWarehouses] = await Promise.all([
            fetchAllData("items/list"),
            fetchAllData("warehouses/list"),
        ]);

        setItems(allItems);
        setWarehouses(allWarehouses);

    } catch (error) {
        console.error("Failed to fetch dropdown data:", error);
        showMessage("Failed to load dropdown data", "error");
    } finally {
        setLoading(false);
    }
};

    useEffect(() => {

        if (!id || id === "undefined" || id === "null" || id.trim() === "") {
            setIsEditMode(false);
            reset();
            fetchDropdownData();
            return;
        }


        fetchDropdownData();
    }, [id, reset]);

    const onSubmit = async (data: any) => {
        try {
            setLoading(true);
            const requestBody: StockVerificationData = {
                ItemId: data.ItemId || "",
                WarehouseId: data.WarehouseId || "",
                PhysicalQty: parseInt(data.PhysicalQty) || 0,

            };


            let response;

            response = await callAPI("stock/verification", Method.POST, requestBody);

            if (response?.success) {
                showMessage(
                    "Opening stock created successfully!",
                    "success"
                );
                navigate("/verification/list");
            } else {
                showMessage(response?.message || "Operation failed", "error");
            }
        } catch (error: any) {
            console.error("❌ Submit error:", error);
            showMessage(error?.message || "Operation failed", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = () => {
        if (!id) return;
        setSelectedId(id);
        setPopupOpen(true);
    };



    return (
        <Box sx={{ p: 2, background: "#f0f0f0", minHeight: "100vh" }}>

            { }
            <Box display="flex" justifyContent="space-between" mb={4}>
                <Typography sx={{ fontSize: "20px", fontWeight: 600 }}>
                    Verification Management
                </Typography>

                { }
            </Box>

            { }
            <Paper
                sx={{
                    borderRadius: "16px",
                    p: 3,
                    background: "#fafafa",
                }}
            >
                { }
                <Box display="flex" justifyContent="space-between" mb={7}>
                    <Typography sx={{ fontSize: "18px", fontWeight: 500 }}>
                        Verification Details
                    </Typography>

                    <Tooltip title={isEditMode ? "Update Verification" : "Create Verification"} arrow>
                        <IconButton
                            sx={{ background: "#000", color: "#fff" }}
                            onClick={handleSubmit(onSubmit)}
                            disabled={!isValid || loading}
                        >
                            <SaveIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>

                <Grid container rowSpacing={8} columnSpacing={5}>




                    <Grid item xs={4}>
                        <WSSelectField
                            name="ItemId"
                            label="Item Name"
                            id="ItemId"
                            control={control}
                            inlineSearch
                            matchAnyWord
                            options={items.map(item => ({
                                value: item.ItemId,
                                label: item.ItemName
                            }))}
                        />
                    </Grid>

                    <Grid item xs="auto" display="flex" alignItems="flex-start" sx={{ pl: '12px !important' }}>
                        <Box
                            onClick={() =>
                                navigate("/verification/itemmaster", { state: { from: "verification" } })
                            }
                            sx={{
                                height: "36px",
                                width: "36px",
                                borderRadius: "10px",
                                background: "#f3f3f3",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                flexShrink: 0,
                                border: "1px solid #d1d5db",
                                "&:hover": {
                                    background: "#e0e0e0",
                                },
                            }}
                        >
                            <AddIcon fontSize="small" />
                        </Box>
                    </Grid>

                    { }
                    <Grid item xs={4}>
                        <WSSelectField
                            name="WarehouseId"
                            label="Warehouse"
                            id="WarehouseId"
                            control={control}
                            inlineSearch
                            options={warehouses.map(warehouse => ({
                                value: warehouse.WarehouseId,
                                label: warehouse.WarehouseName
                            }))}
                        />
                    </Grid>

                    <Grid item xs="auto" display="flex" alignItems="flex-start" sx={{ pl: '12px !important' }}>
                        <Box
                            onClick={() =>
                                navigate("/verification/Warehouse", { state: { from: "verification" } })
                            }
                            sx={{
                                height: "36px",
                                width: "36px",
                                borderRadius: "10px",
                                background: "#f3f3f3",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                flexShrink: 0,
                                border: "1px solid #d1d5db",
                                "&:hover": {
                                    background: "#e0e0e0",
                                },
                            }}
                        >
                            <AddIcon fontSize="small" />
                        </Box>
                    </Grid>

                    { }
                    <Grid item xs={4}>
                        <WSTextField
                            name="PhysicalQty"
                            label="Physical Quantity"
                            id="PhysicalQty"
                            control={control}
                            type={TextType.AllowOnlyNumber}
                        />
                    </Grid>





                </Grid>
            </Paper>
        </Box>
    );
};

export default Verification;

